import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Cookie } from 'lucide-react';
import logoFull from '@/assets/logo-full.svg';
import { AppFooter } from '@/components/layout/AppFooter';
import { getCookiePolicy } from '@/content/cookiePolicy';
import { openCookieSettings } from '@/lib/cookieConsent';

const CookiePolicy = () => {
  const { i18n } = useTranslation();
  const content = getCookiePolicy(i18n.language);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      <div className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#0880AE] hover:text-[#076A8E] transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            {content.back}
          </Link>

          <img src={logoFull} alt="Velox" className="h-8 mb-8" loading="lazy" />

          <h1 className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca'] mb-3">{content.title}</h1>
          <p className="text-base text-[#2D3748] mb-2">{content.subtitle}</p>
          <p className="text-sm text-[#5A6B7A] mb-6">{content.lastUpdated}</p>

          <button
            onClick={openCookieSettings}
            className="inline-flex items-center gap-2 bg-[#FFBF07] hover:bg-[#E5AB06] active:bg-[#CC9805] text-[#2D3748] font-semibold px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 mb-8"
          >
            <Cookie size={18} />
            {content.manageButton}
          </button>

          <div className="space-y-6">
            {content.sections.slice(0, 5).map((section) => (
              <section key={section.title} className="bg-white rounded-xl border border-[#DBE2EA] p-6">
                <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-3">{section.title}</h2>
                <p className="text-sm text-[#2D3748] whitespace-pre-line leading-relaxed">{section.body}</p>
              </section>
            ))}

            {/* Cookie table */}
            <section className="bg-white rounded-xl border border-[#DBE2EA] p-6">
              <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-3">{content.tableTitle}</h2>
              <p className="text-sm text-[#2D3748] leading-relaxed mb-4">{content.tableIntro}</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-[#DBE2EA] rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-[#EBF4F8] text-left">
                      {[
                        content.tableHeaders.name,
                        content.tableHeaders.category,
                        content.tableHeaders.purpose,
                        content.tableHeaders.provider,
                        content.tableHeaders.duration,
                      ].map((header) => (
                        <th key={header} className="p-3 text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {content.rows.map((row, index) => (
                      <tr key={row.name} className={index % 2 === 1 ? 'bg-[#F8F9FA]' : 'bg-white'}>
                        <td className="p-3 text-[#2D3748] font-medium whitespace-nowrap">{row.name}</td>
                        <td className="p-3 text-[#5A6B7A]">{row.category}</td>
                        <td className="p-3 text-[#2D3748]">{row.purpose}</td>
                        <td className="p-3 text-[#5A6B7A]">{row.provider}</td>
                        <td className="p-3 text-[#5A6B7A] whitespace-nowrap">{row.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-[#5A6B7A] leading-relaxed mt-4">{content.tableNote}</p>
            </section>

            {content.sections.slice(5).map((section) => (
              <section key={section.title} className="bg-white rounded-xl border border-[#DBE2EA] p-6">
                <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-3">{section.title}</h2>
                <p className="text-sm text-[#2D3748] whitespace-pre-line leading-relaxed">{section.body}</p>
              </section>
            ))}
          </div>
        </div>
      </div>
      <AppFooter variant="app" />
    </div>
  );
};

export default CookiePolicy;
